/**
 * AI 水印相关工具函数
 */

/** AI 水印类型 */
export enum AiWatermarkType {
  /** 稿定 AI 水印 */
  GD_AI_WATERMARK = "gd-ai-watermark",
  /** AI 水印 */
  AI_WATERMARK = "ai-watermark",
}

/** AI 水印图片地址 */
export const AI_MATERIAL_WATERMARK_URL =
  "https://cdn.dancf.com/fe-assets/20251107/26819c93f3802ddff36cca618d046f87.png";
export const AI_TAG_URL = AI_MATERIAL_WATERMARK_URL;
export const GD_AI_TAG_URL = AI_MATERIAL_WATERMARK_URL;

/**
 * 加载图像
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "Anonymous";
    image.onload = () => {
      resolve(image);
    };
    image.onerror = reject;
    image.src = url;
  });
}

/**
 * 获取 Canvas 的 Blob
 */
function getCanvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to convert canvas to blob"));
        return;
      }
      resolve(blob);
    });
  });
}

interface WatermarkConfig {
  /** 水印类型 */
  type: AiWatermarkType;
  /** 用户名 */
  userName?: string;
  /** 原图宽度 */
  originalWidth: number;
  /** 原图高度 */
  originalHeight: number;
  /** 自定义水印图片URL */
  customWatermarkUrl?: string;
}

/**
 * 创建AI水印Canvas并返回位置信息
 */
async function createAIWatermarkCanvas(
  config: WatermarkConfig
): Promise<{ x: number; y: number; canvas: HTMLCanvasElement }> {
  const { type, userName, originalWidth, originalHeight, customWatermarkUrl } =
    config;

  const isGdAiWatermark = type === AiWatermarkType.GD_AI_WATERMARK;
  const watermarkUrl =
    customWatermarkUrl || (isGdAiWatermark ? GD_AI_TAG_URL : AI_TAG_URL);
  const watermark = await loadImage(watermarkUrl);

  // 创建水印 Canvas
  const watermarkCanvas = document.createElement("canvas");
  const watermarkCtx = watermarkCanvas.getContext("2d");

  if (!watermarkCtx) {
    throw new Error("Failed to get watermark canvas context");
  }

  // 水印 Logo 的尺寸
  const baseWidth = watermark.width;
  const baseHeight = watermark.height;

  if (isGdAiWatermark) {
    // 稿定AI水印处理
    const basePadding = 24;
    const userNameFontSize = 18;
    const userNamePaddingTop = 4;

    // 设置水印 Canvas 尺寸，包含内边距
    watermarkCanvas.width = baseWidth + basePadding * 2;
    const userNameHeight = userName ? userNameFontSize + userNamePaddingTop : 0;
    watermarkCanvas.height = baseHeight + userNameHeight + basePadding * 2;

    // 计算 logo 在水印中的尺寸
    const logoWidth = baseWidth;
    const logoHeight = baseHeight;

    // 绘制水印 Logo
    watermarkCtx.drawImage(
      watermark,
      basePadding,
      basePadding,
      logoWidth,
      logoHeight
    );

    if (userName) {
      // 如果用户名超过11个字符，则截取前11个字符并添加省略号
      const displayName =
        userName.length > 11 ? userName.slice(0, 11) + "..." : userName;

      // 设置文字样式
      watermarkCtx.fillStyle = "#fff";
      watermarkCtx.font = `600 ${userNameFontSize}px sans-serif`;
      watermarkCtx.textAlign = "left";
      watermarkCtx.textBaseline = "top";

      // Add stroke effect
      watermarkCtx.strokeStyle = "rgba(0, 0, 0, 0.08)";
      watermarkCtx.lineWidth = 2;

      const userNameContent = `@ ${displayName}`;
      const userNameWidth = watermarkCtx.measureText(userNameContent).width;

      /* Draw stroke & fill text */
      const drawX = logoWidth - userNameWidth + basePadding - 5;
      const drawY = logoHeight + userNamePaddingTop + basePadding;
      watermarkCtx.strokeText(userNameContent, drawX, drawY);
      watermarkCtx.fillText(userNameContent, drawX, drawY);
    }

    // 计算最终水印在目标图片上的尺寸和位置
    // 目标宽度为原图宽度的 30%
    const targetWidth = originalWidth * 0.3;
    const scale = targetWidth / watermarkCanvas.width;
    const targetHeight = watermarkCanvas.height * scale;

    // 计算水印在目标 Canvas 上的位置
    const x = originalWidth - targetWidth;
    const y = originalHeight - targetHeight;

    // 创建最终的水印Canvas
    const finalWatermarkCanvas = document.createElement("canvas");
    const finalCtx = finalWatermarkCanvas.getContext("2d");

    if (!finalCtx) {
      throw new Error("Failed to get final canvas context");
    }

    finalWatermarkCanvas.width = originalWidth;
    finalWatermarkCanvas.height = originalHeight;

    // 将整个水印 Canvas 绘制到目标 Canvas 上，进行整体缩放
    finalCtx.drawImage(watermarkCanvas, x, y, targetWidth, targetHeight);

    return {
      x,
      y,
      canvas: finalWatermarkCanvas,
    };
  } else {
    // 普通AI水印处理
    const basePadding = 12;

    // 设置水印 Canvas 尺寸，包含内边距
    watermarkCanvas.width = baseWidth + basePadding * 2;
    watermarkCanvas.height = baseHeight + basePadding * 2;

    // 计算 logo 在水印中的尺寸
    const logoWidth = baseWidth;
    const logoHeight = baseHeight;

    // 绘制水印 Logo
    watermarkCtx.drawImage(
      watermark,
      basePadding,
      basePadding,
      logoWidth,
      logoHeight
    );

    // 计算最终水印在目标图片上的尺寸和位置
    const minSide = Math.min(originalWidth, originalHeight);
    // 目标高度为原图短边的 6%
    const targetHeight = minSide * 0.06;
    const scale = targetHeight / watermarkCanvas.height;
    const targetWidth = watermarkCanvas.width * scale;

    // 计算水印在目标 Canvas 上的位置
    const x = originalWidth - targetWidth;
    const y = originalHeight - targetHeight;

    // 创建最终的水印Canvas
    const finalWatermarkCanvas = document.createElement("canvas");
    const finalCtx = finalWatermarkCanvas.getContext("2d");

    if (!finalCtx) {
      throw new Error("Failed to get final canvas context");
    }

    finalWatermarkCanvas.width = originalWidth;
    finalWatermarkCanvas.height = originalHeight;

    // 将整个水印 Canvas 绘制到目标 Canvas 上，进行整体缩放
    finalCtx.drawImage(watermarkCanvas, x, y, targetWidth, targetHeight);

    return {
      x,
      y,
      canvas: finalWatermarkCanvas,
    };
  }
}

/**
 * 绘制水印到Canvas
 */
async function drawAIWatermarkToCanvas(
  config: WatermarkConfig
): Promise<HTMLCanvasElement> {
  return (await createAIWatermarkCanvas(config)).canvas;
}

/**
 * 导出图片，返回 Blob（带水印）
 *
 * @param imageUrl 图片地址
 * @param drawWatermark 是否绘制水印
 * @param aiWatermarkType AI 水印类型
 * @param userName 用户名，如果有传入，则会在水印下方显示
 * @param customWatermarkUrl 自定义水印图片URL
 */
export async function exportImageWithWatermark(
  imageUrl: string,
  drawWatermark = true,
  aiWatermarkType = AiWatermarkType.GD_AI_WATERMARK,
  userName?: string,
  customWatermarkUrl?: string
): Promise<Blob> {
  const image = await loadImage(imageUrl);

  const downloadImageCanvas = document.createElement("canvas");
  const ctx = downloadImageCanvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  // 设置Canvas尺寸
  downloadImageCanvas.width = image.naturalWidth;
  downloadImageCanvas.height = image.naturalHeight;

  // 绘制图片
  ctx.drawImage(image, 0, 0);

  /* 绘制水印 Logo */
  if (drawWatermark) {
    const watermarkCanvas = await drawAIWatermarkToCanvas({
      type: aiWatermarkType,
      userName,
      originalWidth: downloadImageCanvas.width,
      originalHeight: downloadImageCanvas.height,
      customWatermarkUrl,
    });

    // 将水印绘制到主Canvas上
    ctx.drawImage(watermarkCanvas, 0, 0);
  }

  const blob = await getCanvasBlob(downloadImageCanvas);
  return blob;
}

/**
 * 下载 Blob 文件
 */
export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
}
